import { useCallback, useEffect, useMemo, useRef } from 'react';
import useAppStore from '../store';
import { listApprovals, updateApproval } from '../lib/api/approvals';
import { createOptimisticApprovalQueue } from '../features/approvals/optimistic';

/**
 * Approval queue data hook.
 *
 * Mount: GET /api/approvals. Success → slice holds the server queue and
 * approvalsMode flips to 'live'; failure (404 until the backend slice lands,
 * auth, network) → sample seed stays and mode stays 'sample' — the DataMode
 * badge renders the honest state either way (go-live-readiness §1/§5c2).
 *
 * Actions (approve/reject/modify) run through the optimistic queue
 * (features/approvals/optimistic.js): the row is hidden immediately, PATCH
 * /api/approvals/:id commits on success and rolls the row back on error.
 * Backend PATCH is dry-run (log-only) so UI behavior is visually unchanged.
 */
function useApprovals() {
  const approvals = useAppStore((state) => state.approvals);
  const approvalsMode = useAppStore((state) => state.approvalsMode);
  const selectedApprovalId = useAppStore((state) => state.selectedApprovalId);
  const setSelectedApproval = useAppStore((state) => state.setSelectedApproval);
  const setApprovals = useAppStore((state) => state.setApprovals);
  const setApprovalsMode = useAppStore((state) => state.setApprovalsMode);
  const approveApproval = useAppStore((state) => state.approveApproval);
  const rejectApproval = useAppStore((state) => state.rejectApproval);
  const modifyApproval = useAppStore((state) => state.modifyApproval);
  const removeApproval = useAppStore((state) => state.removeApproval);
  const restoreApproval = useAppStore((state) => state.restoreApproval);

  // The optimistic queue needs stable remove/restore — build it once.
  const queue = useMemo(
    () => createOptimisticApprovalQueue({ remove: removeApproval, restore: restoreApproval }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const queueRef = useRef(queue);
  queueRef.current = queue;

  useEffect(() => {
    let cancelled = false;

    listApprovals()
      .then((rows) => {
        if (cancelled) return;
        setApprovals(Array.isArray(rows) ? rows : [], 'live');
      })
      .catch(() => {
        // Keep the sample seed; mode stays 'sample' for the honesty badge.
        if (cancelled) return;
        setApprovalsMode();
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actOnApproval = useCallback((id, action) => {
    const { apply, commit, rollback } = queueRef.current;

    if (action === 'modified') {
      // Modify keeps the row (status flips); hide/restore semantics don't
      // apply — commit is the slice update itself.
      modifyApproval(id);
      updateApproval(id, action).catch(() => {});
      return;
    }

    if (action === 'approved' || action === 'rejected') {
      apply(id); // snapshot + hide immediately
      updateApproval(id, action)
        .then(() => commit(id)) // server accepted — row stays gone
        .catch(() => rollback(id)); // server rejected — row comes back
    }
    // Unknown action — tolerate like before (no state change, no request).
  }, [modifyApproval]);

  return {
    approvals,
    approvalsMode,
    selectedApprovalId,
    setSelectedApproval,
    actOnApproval
  };
}

export default useApprovals;
